import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import UserDetail from "../components/UserDetail";
import UserDeleteDialog from "../components/dialog/UserDeleteDialog";
import { getUserList } from "../services/callApi";
import { API_QUERY_KEYS, invalidateApiQueries } from "../utils/apiQueryCache";
import {
  MANAGEMENT_ACTION_CELL_SX,
  MANAGEMENT_BODY_CELL_SX,
  MANAGEMENT_HEADER_CELL_SX,
  MANAGEMENT_ROWS_PER_PAGE,
  MANAGEMENT_SPACER_BODY_CELL_SX,
  MANAGEMENT_SPACER_HEADER_CELL_SX,
  MANAGEMENT_TABLE_SX,
  ManagementEmptyState,
  ManagementIconActionButton,
  ManagementListPage,
  ManagementListPagination,
  ManagementTableContainer,
  ManagementTruncatedText,
} from "./common/ManagementListLayout";

const formatRegisteredAt = (dateStr) => {
  if (!dateStr) {
    return "－";
  }

  const date = new Date(dateStr.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) {
    return dateStr;
  }

  const pad = (value) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const getRegisteredDate = (dateStr) => {
  const dateMatch = String(dateStr ?? "").match(/^\d{4}-\d{2}-\d{2}/);
  return dateMatch ? dateMatch[0] : "";
};

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSelect, setIsSelect] = useState(false);
  const [userId, setUserId] = useState(null);
  const [page, setPage] = useState(0);
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [searchUserName, setSearchUserName] = useState("");
  const [searchRegisteredFrom, setSearchRegisteredFrom] = useState("");
  const [searchRegisteredTo, setSearchRegisteredTo] = useState("");
  const [searchUnderScore, setSearchUnderScore] = useState("");
  const [searchOverScore, setSearchOverScore] = useState("");

  const fetchUserList = useCallback(async () => {
    try {
      const fetchedUsers = await getUserList();
      const sortedUsers = Array.isArray(fetchedUsers)
        ? [...fetchedUsers].sort((a, b) => b.userId - a.userId)
        : [];
      setUsers(sortedUsers);
    } catch (error) {
      console.error("ユーザー一覧の取得に失敗しました。");
      alert("ユーザー一覧の取得に失敗しました。\nページの再読み込みをしてください。");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserList();
  }, [fetchUserList]);

  const refreshUserList = useCallback(async () => {
    invalidateApiQueries(API_QUERY_KEYS.userList);
    await fetchUserList();
  }, [fetchUserList]);

  const filteredUsers = users.filter((user) => {
    const matchName = searchUserName === "" || user.name?.includes(searchUserName);
    const matchUnderScore =
      searchUnderScore === "" ||
      (user.totalScore !== null && Number(searchUnderScore) <= user.totalScore);
    const matchOverScore =
      searchOverScore === "" ||
      (user.totalScore !== null && user.totalScore <= Number(searchOverScore));
    const registeredDate = getRegisteredDate(user.registeredAt);
    const matchRegisteredFrom =
      searchRegisteredFrom === "" ||
      (registeredDate !== "" && registeredDate >= searchRegisteredFrom);
    const matchRegisteredTo =
      searchRegisteredTo === "" ||
      (registeredDate !== "" && registeredDate <= searchRegisteredTo);

    return matchName && matchUnderScore && matchOverScore && matchRegisteredFrom && matchRegisteredTo;
  });
  const paginatedUsers = filteredUsers.slice(
    page * MANAGEMENT_ROWS_PER_PAGE,
    page * MANAGEMENT_ROWS_PER_PAGE + MANAGEMENT_ROWS_PER_PAGE
  );

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredUsers.length / MANAGEMENT_ROWS_PER_PAGE) - 1);
    setPage((currentPage) => Math.min(currentPage, lastPage));
  }, [filteredUsers.length]);

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  };

  const filterToolbar = (
    <Box>
      <Button
        variant="outlined"
        startIcon={isFilterVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        onClick={() => setIsFilterVisible((current) => !current)}
      >
        {isFilterVisible ? "絞り込み条件を隠す" : "絞り込み条件を表示"}
      </Button>
      <Collapse in={isFilterVisible} timeout="auto" unmountOnExit>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "120px minmax(0, 1fr)" },
            gap: 1.5,
            alignItems: "center",
            mt: 2,
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            名前
          </Typography>
          <TextField
            label="名前で検索"
            value={searchUserName}
            onChange={handleFilterChange(setSearchUserName)}
            size="small"
          />
          <Typography variant="body2" fontWeight={600}>
            登録日時
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              fullWidth
              type="date"
              label="登録開始日"
              value={searchRegisteredFrom}
              onChange={handleFilterChange(setSearchRegisteredFrom)}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              inputProps={{ max: searchRegisteredTo || undefined }}
            />
            <Typography color="text.secondary">～</Typography>
            <TextField
              fullWidth
              type="date"
              label="登録終了日"
              value={searchRegisteredTo}
              onChange={handleFilterChange(setSearchRegisteredTo)}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              inputProps={{ min: searchRegisteredFrom || undefined }}
            />
          </Box>
          <Typography variant="body2" fontWeight={600}>
            合計点数
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TextField
              fullWidth
              label="最小点数"
              value={searchUnderScore}
              onChange={handleFilterChange(setSearchUnderScore)}
              size="small"
              inputProps={{ inputMode: "numeric" }}
            />
            <Typography color="text.secondary">～</Typography>
            <TextField
              fullWidth
              label="最大点数"
              value={searchOverScore}
              onChange={handleFilterChange(setSearchOverScore)}
              size="small"
              inputProps={{ inputMode: "numeric" }}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );

  if (loading) {
    return (
      <ManagementListPage title="ユーザー一覧" count={0}>
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 240 }}>
          <CircularProgress />
        </Box>
      </ManagementListPage>
    );
  }

  return (
    <Box>
      <Box hidden={isSelect}>
        <ManagementListPage
          title="ユーザー一覧"
          count={filteredUsers.length}
          toolbar={filterToolbar}
        >
          {filteredUsers.length === 0 ? (
            <ManagementEmptyState>
              {users.length === 0 ? "登録されているユーザーはいません。" : "条件に一致するユーザーはいません。"}
            </ManagementEmptyState>
          ) : (
            <ManagementTableContainer>
              <Table sx={{ ...MANAGEMENT_TABLE_SX, minWidth: 860 }} aria-label="ユーザー一覧">
                <colgroup>
                  <col style={{ width: 72 }} />
                  <col style={{ width: 160 }} />
                  <col style={{ width: 180 }} />
                  <col style={{ width: 160 }} />
                  <col style={{ width: 96 }} />
                  <col />
                  <col style={{ width: 72 }} />
                  <col style={{ width: 72 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell align="right" sx={MANAGEMENT_HEADER_CELL_SX}>ID</TableCell>
                    <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>登録日時</TableCell>
                    <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>名前</TableCell>
                    <TableCell sx={MANAGEMENT_HEADER_CELL_SX}>ふりがな</TableCell>
                    <TableCell align="right" sx={MANAGEMENT_HEADER_CELL_SX}>合計点数</TableCell>
                    <TableCell aria-hidden="true" sx={MANAGEMENT_SPACER_HEADER_CELL_SX} />
                    <TableCell align="center" sx={MANAGEMENT_HEADER_CELL_SX}>詳細</TableCell>
                    <TableCell align="center" sx={MANAGEMENT_HEADER_CELL_SX}>削除</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers.map((user) => (
                    <TableRow key={user.userId} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell align="right" sx={MANAGEMENT_BODY_CELL_SX}>{user.userId}</TableCell>
                      <TableCell sx={MANAGEMENT_BODY_CELL_SX}>
                        <ManagementTruncatedText>{formatRegisteredAt(user.registeredAt)}</ManagementTruncatedText>
                      </TableCell>
                      <TableCell sx={MANAGEMENT_BODY_CELL_SX}>
                        <ManagementTruncatedText>{user.name}</ManagementTruncatedText>
                      </TableCell>
                      <TableCell sx={MANAGEMENT_BODY_CELL_SX}>
                        <ManagementTruncatedText>{user.furigana}</ManagementTruncatedText>
                      </TableCell>
                      <TableCell align="right" sx={MANAGEMENT_BODY_CELL_SX}>{user.totalScore ?? "－"}</TableCell>
                      <TableCell aria-hidden="true" sx={MANAGEMENT_SPACER_BODY_CELL_SX} />
                      <TableCell align="center" sx={MANAGEMENT_ACTION_CELL_SX}>
                        <ManagementIconActionButton
                          ariaLabel={`${user.name}を詳細表示`}
                          tooltip="詳細"
                          onClick={() => {
                            setUserId(user.userId);
                            setIsSelect(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </ManagementIconActionButton>
                      </TableCell>
                      <TableCell align="center" sx={MANAGEMENT_ACTION_CELL_SX}>
                        <UserDeleteDialog user={user} onDeleted={refreshUserList} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <ManagementListPagination
                count={filteredUsers.length}
                page={page}
                onPageChange={(_, nextPage) => setPage(nextPage)}
              />
            </ManagementTableContainer>
          )}
        </ManagementListPage>
      </Box>
      <Box hidden={!isSelect}>
        <UserDetail
          userId={userId}
          onBack={() => setIsSelect(false)}
          onUpdated={refreshUserList}
        />
      </Box>
    </Box>
  );
};

export default UserList;
